import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { LayerConfiguration } from "../layers/models";
import { useHistory } from "react-router";
import * as Routes from "routes";
import { explorerLayerIdToProductCodeMappings } from "pages/geoscape-data/constants";

interface ColorPickerProps {
  expandedLayer: LayerConfiguration | null;
  onChangeColor: (id: string, color: string) => void;
  onChangeOpacity: (id: string, opacity: number) => void;
  onChangeThickness: (id: string, thickness: number) => void;
  onChangeDiameter: (id: string, diameter: number) => void;
  onChangeFill: (id: string, fill: boolean) => void;
  zoomToLayerFn: () => void;
}

export const hexCodes: string[] = [
  "#D50000",
  "#C51162",
  "#AA00FF",
  "#6200EA",
  "#304FFE",
  "#2962FF",
  "#0091EA",
  "#00B8D4",
  "#00BFA5",
  "#00C853",
  "#64DD17",
  "#AEEA00",
  "#FFD600",
  "#FFAB00",
  "#FF6D00",
  "#DD2C00",
];

export const ColorPicker = (props: ColorPickerProps) => {
  const handleSliderOpacityChange = (value: number[]) => {
    if (!props.expandedLayer) return;
    props.onChangeOpacity(props.expandedLayer.id, value[0] / 100);
  };

  const handleSliderThicknessChange = (value: number[]) => {
    if (!props.expandedLayer) return;
    props.onChangeThickness(props.expandedLayer.id, value[0]);
  };

  const handleSliderDiameterChange = (value: number[]) => {
    if (!props.expandedLayer) return;
    props.onChangeDiameter(props.expandedLayer.id, value[0]);
  };

  const handleColorChange = (color: string) => {
    if (!props.expandedLayer) return;
    props.onChangeColor(props.expandedLayer.id, color);
  };

  const handleFillChange = (checked: boolean) => {
    if (!props.expandedLayer) return;
    props.onChangeFill(props.expandedLayer.id, checked);
  };

  const history = useHistory();
  const productCode = props.expandedLayer
    ? explorerLayerIdToProductCodeMappings.get(props.expandedLayer.id)
    : "";

  const max_opacity = 100;
  const min_opacity = 0;
  const max_thickness = 10;
  const min_thickness = 0;
  const max_diameter = 10;
  const min_diameter = 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Opacity */}
      <div className="space-y-2">
        <Label className="text-sm">Opacity</Label>
        <Slider
          value={[
            props.expandedLayer?.styles.opacity != undefined
              ? props.expandedLayer.styles.opacity * 100
              : 20,
          ]}
          onValueChange={handleSliderOpacityChange}
          min={min_opacity}
          max={max_opacity}
          step={10}
        />
        <div className="flex justify-between">
          <button
            className="text-xs text-muted-foreground cursor-pointer hover:text-foreground"
            onClick={() => handleSliderOpacityChange([min_opacity])}
          >
            {min_opacity}%
          </button>
          <button
            className="text-xs text-muted-foreground cursor-pointer hover:text-foreground"
            onClick={() => handleSliderOpacityChange([max_opacity])}
          >
            {max_opacity}%
          </button>
        </div>
      </div>

      {/* Thickness — for Polygon and LineString */}
      {(props.expandedLayer?.styles.type == "Polygon" ||
        props.expandedLayer?.styles.type == "LineString") && (
        <div className="space-y-2">
          <Label className="text-sm">Thickness</Label>
          <Slider
            value={[
              props.expandedLayer?.styles.thickness != undefined
                ? props.expandedLayer.styles.thickness
                : 2,
            ]}
            onValueChange={handleSliderThicknessChange}
            min={min_thickness}
            max={max_thickness}
            step={1}
          />
          <div className="flex justify-between">
            <button
              className="text-xs text-muted-foreground cursor-pointer hover:text-foreground"
              onClick={() => handleSliderThicknessChange([min_thickness])}
            >
              {min_thickness}px
            </button>
            <button
              className="text-xs text-muted-foreground cursor-pointer hover:text-foreground"
              onClick={() => handleSliderThicknessChange([max_thickness])}
            >
              {max_thickness}px
            </button>
          </div>
        </div>
      )}

      {/* Diameter — for Point */}
      {props.expandedLayer?.styles.type == "Point" && (
        <div className="space-y-2">
          <Label className="text-sm">Diameter</Label>
          <Slider
            value={[
              props.expandedLayer?.styles.diameter != undefined
                ? props.expandedLayer.styles.diameter
                : 2,
            ]}
            onValueChange={handleSliderDiameterChange}
            min={min_diameter}
            max={max_diameter}
            step={1}
          />
          <div className="flex justify-between">
            <button
              className="text-xs text-muted-foreground cursor-pointer hover:text-foreground"
              onClick={() => handleSliderDiameterChange([min_diameter])}
            >
              {min_diameter}px
            </button>
            <button
              className="text-xs text-muted-foreground cursor-pointer hover:text-foreground"
              onClick={() => handleSliderDiameterChange([max_diameter])}
            >
              {max_diameter}px
            </button>
          </div>
        </div>
      )}

      {/* Fill toggle — for Polygon */}
      {props.expandedLayer?.styles.type == "Polygon" && (
        <div className="flex items-center gap-2">
          <Switch
            checked={props.expandedLayer?.styles.fill}
            onCheckedChange={handleFillChange}
          />
          <Label className="text-sm">Fill</Label>
        </div>
      )}

      {/* Color Picker */}
      <div className="space-y-2">
        <Label className="text-sm">Color Picker</Label>
        <div className="grid grid-cols-4">
          {hexCodes.map((hexValue) => (
            <button
              key={hexValue}
              className="w-[50px] h-[50px] cursor-pointer border-0"
              style={{ backgroundColor: hexValue }}
              onClick={() => handleColorChange(hexValue)}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <button
        className="w-full text-left px-2 py-1.5 text-xs rounded-md hover:bg-accent cursor-pointer"
        onClick={props.zoomToLayerFn}
      >
        Zoom to Layer
      </button>
      {productCode && (
        <button
          className="w-full text-left px-2 py-1.5 text-xs rounded-md hover:bg-accent cursor-pointer"
          onClick={() =>
            history.push(`${Routes.dataCatalogue}/${productCode}`)
          }
        >
          Open in Catalogue
        </button>
      )}
    </div>
  );
};
